/** @jsx jsx */

import { css, jsx } from '@emotion/core';

import {
  useQueryPartnerDashboard,
  useQueryPartnerDashboardUsage,
} from 'app/developer-v3/hooks';

import { TierAction } from '../Dashboard';
import ActiveUsers from './ActiveUsers';
import HealthScore from './HealthScore';
import { EmbedStatus } from './EmbedStatus';
import { queryOptions } from '../constants';
import { useEmbedConversionPercentage } from 'app/developer-v3/pages/insights/tiles/EmbedInsightsTile';

const styleRoot = ({ isBeta }: { isBeta: boolean }) => css`
  margin-top: 20px;
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: ${isBeta ? 'auto' : '1fr'};
  gap: 20px;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1250px) {
    grid-template-columns: ${isBeta ? '1fr 1fr' : '1fr 1fr 1fr'};
  }
`;

type Props = {
  appId: number;
  isBeta: boolean;
  serviceId: number;
};

export default function Stats({ appId, isBeta, serviceId }: Props) {
  const queryPartnerDashboard = useQueryPartnerDashboard(
    { serviceId },
    queryOptions
  );
  const queryPartnerDashboardUsageQuarterly = useQueryPartnerDashboardUsage(
    { period: 'quarter', serviceId },
    queryOptions
  );
  const queryPartnerDashboardUsageMonthly = useQueryPartnerDashboardUsage(
    { period: 'month', serviceId },
    queryOptions
  );

  const { data: partnerDashboard } = queryPartnerDashboard;
  const isLoading =
    queryPartnerDashboardUsageMonthly.isLoading ||
    queryPartnerDashboardUsageQuarterly.isLoading;
  const error =
    queryPartnerDashboardUsageMonthly.error ||
    queryPartnerDashboardUsageQuarterly.error;

  const conversionRate = useEmbedConversionPercentage({ appId });

  if (isLoading || error) {
    return null;
  }

  return (
    <div css={styleRoot({ isBeta })}>
      {isBeta && (
        <TierAction
          isBeta={isBeta}
          statusDeadline={partnerDashboard.status.deadline}
          statusTier={partnerDashboard.status.tier}
        />
      )}
      <EmbedStatus
        appId={appId}
        conversionRate={conversionRate}
        isBeta={isBeta}
      />
      <ActiveUsers
        isBeta={isBeta}
        partnerTier={partnerDashboard?.status?.tier}
        usage={{
          currentActive: partnerDashboard?.usage?.currentActive,
          hasCompleted: partnerDashboard?.usage?.hasCompleted,
          statistics: {
            monthly: queryPartnerDashboardUsageMonthly?.data,
            quarterly: queryPartnerDashboardUsageQuarterly?.data,
          },
          tier: partnerDashboard?.usage?.tier,
        }}
      />
      <HealthScore
        appId={appId}
        health={partnerDashboard?.health}
        isBeta={isBeta}
        partnerTier={partnerDashboard?.status?.tier}
      />
    </div>
  );
}
